import React, { useState, useEffect } from 'react';
import { getAllLoads, respondToLoad, updateLoadStatus, submitLoadPod } from '../../ApiController/ApiService';
import { toast } from 'react-toastify';

export default function Loadboard() {
  const [loads, setLoads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLoad, setSelectedLoad] = useState(null);

  // Respond Form
  const [proposedPrice, setProposedPrice] = useState('');
  const [estimatedPickupTime, setEstimatedPickupTime] = useState('');

  // POD Form
  const [podData, setPodData] = useState({
    podImage: '',
    recipientName: '',
    recipientSignature: '',
    podDeliveryLocation: '',
    podNotes: '',
    damageReported: false
  });

  const fetchLoads = async () => {
    try {
      setLoading(true);
      const data = await getAllLoads({ limit: 50 });
      const docs = data?.docs || data?.data?.docs || [];
      setLoads(docs);
    } catch (err) {
      toast.error('Failed to fetch loads');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLoads();
    const interval = setInterval(fetchLoads, 15000); // poll every 15s
    return () => clearInterval(interval);
  }, []);

  const handleRespond = async (e) => {
    e.preventDefault();
    if (!selectedLoad) return;
    try {
      await respondToLoad(selectedLoad._id, {
        response: "Interested",
        proposedPrice: Number(proposedPrice),
        estimatedPickupTime
      });
      toast.success("Successfully submitted your response.");
      setSelectedLoad(null);
      fetchLoads();
    } catch (error) {
      toast.error(error.message || "Failed to respond");
    }
  };

  const handleStatusUpdate = async (status) => {
    if (!selectedLoad) return;
    try {
      await updateLoadStatus(selectedLoad._id, { status });
      toast.success(`Status updated to ${status}`);
      setSelectedLoad({ ...selectedLoad, status });
      fetchLoads();
    } catch (error) {
      toast.error(error.message || "Failed to update status");
    }
  };

  const handlePODSubmit = async (e) => {
    e.preventDefault();
    if (!selectedLoad) return;
    try {
      await submitLoadPod(selectedLoad._id, podData);
      toast.success("POD submitted successfully. Load completed.");
      setSelectedLoad(null);
      fetchLoads();
    } catch (error) {
      toast.error(error.message || "Failed to submit POD");
    }
  };

  // Decode user ID from token (very basic decode for frontend check)
  const getUserIdFromToken = () => {
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) return null;
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const payload = JSON.parse(window.atob(base64));
      return payload._id || payload.id || payload.userId;
    } catch (e) {
      return null;
    }
  };

  const currentUserId = getUserIdFromToken();

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-cyan-400">Available Loads</h2>

      {loading && loads.length === 0 ? (
        <div>Loading...</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {loads.map(load => {
            // Check if carrier has responded
            const hasResponded = load.responses?.some(r => r.carrierId === currentUserId);
            const isAssignedToMe = load.selectedCarrierId && load.selectedCarrierId === currentUserId;

            return (
              <div key={load._id} className="bg-[#1A1F45] border border-[#2A2A4D] p-5 rounded-xl flex flex-col justify-between">
                <div>
                  <div className="flex justify-between items-center mb-4">
                    <span className="font-bold text-white">{load.loadType?.toUpperCase() || 'LOAD'}</span>
                    <span className="text-sm font-semibold text-green-400">${load.deliveryPrice}</span>
                  </div>
                  <p className="text-sm text-gray-300 mb-2"><strong>From:</strong> {load.pickupAddress}</p>
                  <p className="text-sm text-gray-300 mb-4"><strong>To:</strong> {load.deliveryAddress}</p>
                </div>

                {isAssignedToMe ? (
                  <button onClick={() => setSelectedLoad(load)} className="w-full py-2 bg-blue-600 hover:bg-blue-500 rounded text-white font-bold transition">
                    Manage Active Load
                  </button>
                ) : hasResponded ? (
                  <div className="w-full py-2 text-center text-yellow-500 font-semibold bg-yellow-500/10 border border-yellow-500/20 rounded">
                    Waiting for company to select a carrier
                  </div>
                ) : (
                  <button onClick={() => setSelectedLoad(load)} className="w-full py-2 bg-[#7c3aed] hover:bg-[#6d28d9] rounded text-white font-bold transition">
                    Respond
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Modal for Action */}
      {selectedLoad && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70 p-4">
          <div className="bg-[#1A1F45] border border-[#2A2A4D] rounded-xl w-full max-w-lg p-6 relative max-h-[90vh] overflow-y-auto custom-scrollbar">
            <button onClick={() => setSelectedLoad(null)} className="absolute top-4 right-4 text-gray-400 hover:text-white">✕</button>
            <h3 className="text-xl font-bold text-cyan-400 mb-6">Load Action: {selectedLoad.loadType}</h3>

            {/* If assigned to current carrier */}
            {selectedLoad.selectedCarrierId === currentUserId ? (
              <div className="space-y-4">
                <p className="text-green-400 font-semibold mb-4">You have been assigned to this load!</p>

                <h4 className="font-bold text-white">Current Status: {selectedLoad.status || 'Assigned'}</h4>

                <div className="flex flex-col gap-2 mt-4">
                  <button onClick={() => handleStatusUpdate("En Route to Pickup")} className="py-2 bg-blue-600 hover:bg-blue-500 rounded text-white">Mark: En Route to Pickup</button>
                  <button onClick={() => handleStatusUpdate("Picked Up")} className="py-2 bg-indigo-600 hover:bg-indigo-500 rounded text-white">Mark: Picked Up</button>
                  <button onClick={() => handleStatusUpdate("In Transit")} className="py-2 bg-purple-600 hover:bg-purple-500 rounded text-white">Mark: In Transit</button>
                  <button onClick={() => handleStatusUpdate("Delivered")} className="py-2 bg-green-600 hover:bg-green-500 rounded text-white">Mark: Delivered</button>
                </div>

                {selectedLoad.status === 'Delivered' && (
                  <form onSubmit={handlePODSubmit} className="mt-8 pt-6 border-t border-[#2A2A4D] space-y-4">
                    <h4 className="font-bold text-white mb-2">Submit Proof of Delivery</h4>

                    <input type="text" placeholder="Image URL (Temp)" className="w-full p-2 bg-[#0F0A2B] rounded text-white" value={podData.podImage} onChange={e => setPodData({ ...podData, podImage: e.target.value })} required />
                    <input type="text" placeholder="Recipient Name" className="w-full p-2 bg-[#0F0A2B] rounded text-white" value={podData.recipientName} onChange={e => setPodData({ ...podData, recipientName: e.target.value })} required />
                    <input type="text" placeholder="Recipient Signature (Base64/URL)" className="w-full p-2 bg-[#0F0A2B] rounded text-white" value={podData.recipientSignature} onChange={e => setPodData({ ...podData, recipientSignature: e.target.value })} required />
                    <input type="text" placeholder="Delivery Location" className="w-full p-2 bg-[#0F0A2B] rounded text-white" value={podData.podDeliveryLocation} onChange={e => setPodData({ ...podData, podDeliveryLocation: e.target.value })} required />
                    <textarea placeholder="Notes" className="w-full p-2 bg-[#0F0A2B] rounded text-white" value={podData.podNotes} onChange={e => setPodData({ ...podData, podNotes: e.target.value })} />

                    <div className="flex items-center gap-2">
                      <input type="checkbox" id="damage" checked={podData.damageReported} onChange={e => setPodData({ ...podData, damageReported: e.target.checked })} />
                      <label htmlFor="damage" className="text-gray-300">Damage Reported?</label>
                    </div>

                    <button type="submit" className="w-full py-2 bg-green-500 hover:bg-green-400 text-black font-bold rounded">Submit POD</button>
                  </form>
                )}
              </div>
            ) : (
              /* Not assigned yet, show respond form */
              <form onSubmit={handleRespond} className="space-y-4">
                <div>
                  <label className="block text-sm text-gray-300 mb-1">Proposed Price ($)</label>
                  <input type="number" className="w-full p-2 bg-[#0F0A2B] rounded text-white" value={proposedPrice} onChange={e => setProposedPrice(e.target.value)} required />
                </div>
                <div>
                  <label className="block text-sm text-gray-300 mb-1">Estimated Pickup Time</label>
                  <input type="datetime-local" className="w-full p-2 bg-[#0F0A2B] rounded text-white" value={estimatedPickupTime} onChange={e => setEstimatedPickupTime(e.target.value)} required />
                </div>
                <div className="pt-4">
                  <button type="submit" className="w-full py-3 bg-[#7c3aed] hover:bg-[#6d28d9] text-white font-bold rounded transition">
                    Send "Interested" Response
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
